import { useReducer, useState, useCallback, useEffect } from 'react';
import caseFormReducer from 'containers/pages/cases/reducers/caseFormReducer';
import initCase from 'containers/pages/cases/data/initCase';
import initCaseValid, { loadedCaseValid } from 'containers/pages/cases/data/initCaseValid';
import handleCaseField from 'containers/pages/cases/libs/handleCaseField';
import { validateAllFields, validateCustomerId, validateConsultantId, validateStatusId } from 'containers/pages/cases/libs/validateCase';
import { CaseFormData, CaseValidation } from 'containers/pages/cases/types';
import { useNotificationProvider } from 'hooks';
import getCase from 'api/cases/getCase';
import createCase from 'api/cases/createCase';
import updateCase from 'api/cases/updateCase';
import indexCase from 'api/search/indexCase';

interface GPDChangedPayload {
	caseId: number;
	caseTitle: string;
	groupId: number;
	groupTitle: string;
	projectId: number;
	projectTitle: string;
	deliverableId: number;
	deliverableTitle: string;
	casePayload: Record<string, unknown>;
}

interface UseCaseFormProps {
	mode: 'add' | 'edit' | 'copy';
	caseId?: number;
	onSave: () => void;
	onClose: () => void;
	onGPDChanged?: (payload: GPDChangedPayload) => void;
}

function makeCasePayload(form: CaseFormData): Record<string, unknown> {
	return {
		...(form.Id ? { Id: form.Id } : {}),
		Title: form.Title,
		Description: form.Description,
		StatusComment: form.StatusComment,
		StatusId: Number(form.StatusId),
		CustomerId: form.CustomerId,
		ConsultantId: form.ConsultantId,
		PrimaryContactId: form.PrimaryContactId,
		GroupId: form.GroupId,
		ProjectId: form.ProjectId,
		DeliverableId: form.DeliverableId,
		Keywords: form.Keywords,
		References: form.References,
		Private: form.Private,
		IpRestricted: form.IpRestricted,
	};
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function convertApiCaseToFormData(apiCase: any): CaseFormData {
	return {
		Id: apiCase.Id || apiCase.id,
		Title: apiCase.Title || '',
		Description: apiCase.Description || '',
		StatusComment: apiCase.StatusComment || '',
		StatusId: String(apiCase.StatusId || '1'),
		StatusTitle: apiCase.StatusTitle || '',
		CustomerId: apiCase.CustomerId || null,
		CustomerName: apiCase.CustomerName || '',
		ConsultantId: apiCase.ConsultantId || null,
		ConsultantName: apiCase.ConsultantName || '',
		PrimaryContactId: apiCase.PrimaryContactId || null,
		PrimaryContactName: apiCase.PrimaryContactName || '',
		GroupId: apiCase.GroupId || null,
		GroupTitle: apiCase.GroupTitle || '',
		ProjectId: apiCase.ProjectId || null,
		ProjectTitle: apiCase.ProjectTitle || '',
		DeliverableId: apiCase.DeliverableId || null,
		DeliverableTitle: apiCase.DeliverableTitle || '',
		Keywords: apiCase.Keywords?.map((k: { Id: number }) => k.Id) || [],
		References: apiCase.References?.map((r: { Id: number }) => r.Id) || [],
		KeywordTags: apiCase.Keywords?.map((k: { Id: number; Title: string }) => ({ Id: k.Id, Title: k.Title })) || [],
		ReferenceTags: apiCase.References?.map((r: { Id: number; Title: string }) => ({ Id: r.Id, Title: r.Title })) || [],
		Private: apiCase.Private ?? true,
		IpRestricted: apiCase.IpRestricted ?? true,
	};
}

function useCaseForm({ mode, caseId, onSave, onClose, onGPDChanged }: UseCaseFormProps) {
	const { notifyError, notifySuccess } = useNotificationProvider();
	const [formState, dispatch] = useReducer(caseFormReducer, { ...initCase });
	const [valid, setValid] = useState<CaseValidation>({ ...initCaseValid });
	const [isLoading, setIsLoading] = useState(false);
	// Track original GPD values to detect changes
	const [originalGPD, setOriginalGPD] = useState<{ GroupId: number | null; ProjectId: number | null; DeliverableId: number | null } | null>(null);

	// Load case data for edit/copy, reset for add
	useEffect(() => {
		if (mode === 'add') {
			dispatch({ type: 'RESET', payload: { ...initCase } });
			setValid({ ...initCaseValid });
		} else if ((mode === 'edit' || mode === 'copy') && caseId) {
			const loadCase = async () => {
				setIsLoading(true);
				try {
					const result = await getCase(caseId);
					const caseData = result?.Data || result;
					const converted = convertApiCaseToFormData(caseData);
					if (mode === 'copy') {
						delete converted.Id;
					}
					dispatch({ type: 'CASE_CONVERT', payload: converted });
					setValid({ ...loadedCaseValid });
					if (mode === 'edit') {
						setOriginalGPD({
							GroupId: converted.GroupId,
							ProjectId: converted.ProjectId,
							DeliverableId: converted.DeliverableId,
						});
					}
				} catch (error) {
					console.error('[useCaseForm] Failed to load case:', error);
					notifyError('Failed to load case data');
				} finally {
					setIsLoading(false);
				}
			};
			loadCase();
		}
	}, [mode, caseId, notifyError]);

	// Central text field change handler
	const onFieldChange = useCallback(
		(e: { target: { name: string; value: string } }) => {
			handleCaseField(e, dispatch, setValid);
		},
		[],
	);

	// Autocomplete change for Customer
	const onCustomerChange = useCallback(
		(_event: unknown, newValue: { Id: number; Name: string } | null) => {
			if (newValue) {
				dispatch({ type: 'CustomerId', payload: newValue.Id });
				dispatch({ type: 'CustomerName', payload: newValue.Name });
				setValid((prev) => ({ ...prev, CustomerId: validateCustomerId(newValue.Id) }));
			} else {
				dispatch({ type: 'CustomerId', payload: null });
				dispatch({ type: 'CustomerName', payload: '' });
				setValid((prev) => ({ ...prev, CustomerId: validateCustomerId(null) }));
			}
			dispatch({ type: 'CLEAR_DEPENDENTS_CUSTOMER', payload: null });
		},
		[],
	);

	// Autocomplete change for Consultant
	const onConsultantChange = useCallback(
		(_event: unknown, newValue: { Id: number; FullName: string } | null) => {
			if (newValue) {
				dispatch({ type: 'ConsultantId', payload: newValue.Id });
				dispatch({ type: 'ConsultantName', payload: newValue.FullName });
				setValid((prev) => ({ ...prev, ConsultantId: validateConsultantId(newValue.Id) }));
			} else {
				dispatch({ type: 'ConsultantId', payload: null });
				dispatch({ type: 'ConsultantName', payload: '' });
				setValid((prev) => ({ ...prev, ConsultantId: validateConsultantId(null) }));
			}
		},
		[],
	);

	// Autocomplete change for Status
	const onStatusChange = useCallback(
		(_event: unknown, newValue: { Id: number; Title: string } | null) => {
			if (newValue) {
				dispatch({ type: 'StatusId', payload: String(newValue.Id) });
				dispatch({ type: 'StatusTitle', payload: newValue.Title });
				setValid((prev) => ({ ...prev, StatusId: validateStatusId(String(newValue.Id)) }));
			} else {
				dispatch({ type: 'StatusId', payload: '' });
				dispatch({ type: 'StatusTitle', payload: '' });
				setValid((prev) => ({ ...prev, StatusId: validateStatusId('') }));
			}
		},
		[],
	);

	// Autocomplete change for Primary Contact
	const onContactChange = useCallback(
		(_event: unknown, newValue: { Id: number; FullName: string } | null) => {
			dispatch({ type: 'PrimaryContactId', payload: newValue?.Id || null });
			dispatch({ type: 'PrimaryContactName', payload: newValue?.FullName || '' });
		},
		[],
	);

	// GPD autocomplete changes
	const onGroupChange = useCallback(
		(_event: unknown, newValue: { Id: number; Title: string } | null) => {
			dispatch({ type: 'GroupId', payload: newValue?.Id || null });
			dispatch({ type: 'GroupTitle', payload: newValue?.Title || '' });
			dispatch({ type: 'CLEAR_DEPENDENTS_GROUP', payload: null });
		},
		[],
	);

	const onProjectChange = useCallback(
		(_event: unknown, newValue: { Id: number; Title: string } | null) => {
			dispatch({ type: 'ProjectId', payload: newValue?.Id || null });
			dispatch({ type: 'ProjectTitle', payload: newValue?.Title || '' });
			dispatch({ type: 'CLEAR_DEPENDENTS_PROJECT', payload: null });
		},
		[],
	);

	const onDeliverableChange = useCallback(
		(_event: unknown, newValue: { Id: number; Title: string } | null) => {
			dispatch({ type: 'DeliverableId', payload: newValue?.Id || null });
			dispatch({ type: 'DeliverableTitle', payload: newValue?.Title || '' });
		},
		[],
	);

	// Multi-select for Keywords
	const onKeywordsChange = useCallback(
		(_event: unknown, newValue: { Id: number; Title: string }[]) => {
			dispatch({ type: 'Keywords', payload: newValue.map((v) => v.Id) });
			dispatch({ type: 'KeywordTags', payload: newValue });
		},
		[],
	);

	// Multi-select for References
	const onReferencesChange = useCallback(
		(_event: unknown, newValue: { Id: number; Title: string }[]) => {
			dispatch({ type: 'References', payload: newValue.map((v) => v.Id) });
			dispatch({ type: 'ReferenceTags', payload: newValue });
		},
		[],
	);

	// Checkbox changes
	const onCheckboxChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			dispatch({ type: e.target.name, payload: e.target.checked });
		},
		[],
	);

	// Compute canNotSave
	const canNotSave = Object.values(valid).some((v) => v.canNotSave);

	// Save handler
	const onSaveForm = useCallback(async () => {
		const finalValid = validateAllFields(formState);
		const hasErrors = Object.values(finalValid).some((v) => v.canNotSave);
		if (hasErrors) {
			setValid(finalValid);
			return;
		}

		setIsLoading(true);
		try {
			const payload = makeCasePayload(formState);

			// If GPD changed in edit mode, defer saving — open reassignment dialog first
			// so comments can be fetched while they still have the old GPDA
			if (
				mode === 'edit' &&
				onGPDChanged &&
				originalGPD &&
				formState.GroupId &&
				formState.ProjectId &&
				formState.DeliverableId &&
				(formState.GroupId !== originalGPD.GroupId ||
					formState.ProjectId !== originalGPD.ProjectId ||
					formState.DeliverableId !== originalGPD.DeliverableId)
			) {
				onGPDChanged({
					caseId: formState.Id!,
					caseTitle: formState.Title,
					groupId: formState.GroupId,
					groupTitle: formState.GroupTitle,
					projectId: formState.ProjectId,
					projectTitle: formState.ProjectTitle,
					deliverableId: formState.DeliverableId,
					deliverableTitle: formState.DeliverableTitle,
					casePayload: payload,
				});
				// Close the form dialog — the reassignment dialog will handle saving
				onClose();
				return;
			}

			if (mode === 'edit') {
				await updateCase(payload);
				notifySuccess('Case updated successfully');
				// Re-index embedding in background
				if (formState.Id) indexCase(formState.Id);
			} else {
				const result = await createCase(payload);
				notifySuccess('Case created successfully');
				// Re-index new case embedding in background
				const newCaseId = result?.Data?.Id || result?.Id;
				if (newCaseId) indexCase(newCaseId);
			}
			onSave();
			onClose();
		} catch (error) {
			console.error('[useCaseForm] Save failed:', error);
			notifyError(mode === 'edit' ? 'Failed to update case' : 'Failed to create case');
		} finally {
			setIsLoading(false);
		}
	}, [formState, mode, onSave, onClose, onGPDChanged, originalGPD, notifySuccess, notifyError]);

	// Reset handler
	const onReset = useCallback(() => {
		if (mode === 'add') {
			dispatch({ type: 'RESET', payload: { ...initCase } });
			setValid({ ...initCaseValid });
		} else if ((mode === 'edit' || mode === 'copy') && caseId) {
			const reload = async () => {
				setIsLoading(true);
				try {
					const result = await getCase(caseId);
					const caseData = result?.Data || result;
					const converted = convertApiCaseToFormData(caseData);
					if (mode === 'copy') {
						delete converted.Id;
					}
					dispatch({ type: 'CASE_CONVERT', payload: converted });
					setValid({ ...loadedCaseValid });
				} catch (error) {
					console.error('[useCaseForm] Reset failed:', error);
				} finally {
					setIsLoading(false);
				}
			};
			reload();
		}
	}, [mode, caseId]);

	const state = {
		form: formState,
		valid,
		isLoading,
		canNotSave,
		mode,
	};

	const handler = {
		onFieldChange,
		onCustomerChange,
		onConsultantChange,
		onStatusChange,
		onContactChange,
		onGroupChange,
		onProjectChange,
		onDeliverableChange,
		onKeywordsChange,
		onReferencesChange,
		onCheckboxChange,
		onSave: onSaveForm,
		onReset,
	};

	return { state, handler };
}

export default useCaseForm;
